import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { DialogContent, DialogTitle, Grid, Box, LinearProgress } from "@mui/material";
import { SchemesApi } from "apis/SchemesApi";
import { ProductsApi } from "apis/ProductsApi";
import { GroupsApi } from "apis/GroupsApi";
import AutoComplete from "components/AutoComplete";
import { showToast } from "utils";
import ProductsSlabTable from "./ProductsSlabTable";
import {
  getInitialPayload,
  TARGET_APPLIED_ON_OPTIONS,
  PAYOUT_CRITERIA_OPTIONS,
  PAYOUT_SELL_TYPE_OPTIONS,
  TARGET_ACHIEVEMENT_OPTIONS,
  SCHEME_STATUS_OPTIONS,
} from "./constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddSchemeModal = ({ brands, open, setOpen, periodFrom, periodTo, editingScheme, onSaved }) => {
  const [payload, setPayload] = useState(getInitialPayload(periodFrom, periodTo));
  const [isUploading, setIsUploading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [isAiExtracted, setIsAiExtracted] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [saveTimer, setSaveTimer] = useState(10);
  const [pendingRequestPayload, setPendingRequestPayload] = useState(null);
  const fileInputRef = React.useRef(null);
  const timerIntervalRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      if (editingScheme) {
        const s = editingScheme;
        setIsDraft(s.status === "DRAFT");
        const mapProducts = (products) => {
          if (!products) return [];
          return products.map((p) => {
            const productId = p.productId || p.product?.id || p.id;
            const productName = p.productName || `${p.product?.primaryName || ""} ${p.product?.secondaryName || ""}`.trim() || p.primaryName || "";
            const productCode = p.productCode || p.product?.productCode || "";
            return {
              productId,
              productName,
              productCode,
              slabs: p.slabs || [],
            };
          });
        };

        const mapGroups = async (groups, groupIds) => {
          if (groups && Array.isArray(groups) && groups.length > 0 && typeof groups[0] === 'object') {
            return groups;
          }
          if (groupIds && Array.isArray(groupIds) && groupIds.length > 0) {
            try {
              const fetchPromises = groupIds.map(id => GroupsApi.GetGroupsListById(id));
              const responses = await Promise.all(fetchPromises);
              return responses.map(res => res.data?.data).filter(Boolean);
            } catch (err) {
              console.error("Error fetching group details:", err);
              return [];
            }
          }
          return [];
        };

        const mapProductsForAutocomplete = (products) => {
          if (!products) return [];
          return products
            .map((p) => {
              try {
                const product = p.product || p;
                const productId = p.productId || product?.id || p.id;

                if (!productId) return null;

                return {
                  id: productId,
                  primaryName: product?.primaryName || p.productName || "",
                  secondaryName: product?.secondaryName || "",
                };
              } catch (err) {
                return null;
              }
            })
            .filter((p) => p !== null);
        };

        const originalProducts = s.products || s.productsDTO || [];
        const mappedProducts = mapProducts(originalProducts);
        const groupIdsArray = s.groupIds || (s.groups && Array.isArray(s.groups) ? s.groups.map(g => typeof g === 'object' ? g.id : g) : []);

        const setPayloadWithGroups = async () => {
          const fetchedGroups = await mapGroups(s.groups, groupIdsArray);
          setPayload({
            id: s.id,
            name: s.name || "",
            brandId: s.brand?.id || s.brandId || null,
            imageUrl: s.imageUrl || "",
            description: s.description || "",
            termsAndConditions: s.termsAndConditions || "",
            periodFrom: s.periodFrom || periodFrom,
            periodTo: s.periodTo || periodTo,
            isActive: typeof s.isActive === "boolean" ? s.isActive : true,
            isDeleted: typeof s.isDeleted === "boolean" ? s.isDeleted : null,
            status: s.status || "DRAFT",
            isNew: typeof s.isNew === "boolean" ? s.isNew : null,
            isExpiring: typeof s.isExpiring === "boolean" ? s.isExpiring : null,
            groupIds: groupIdsArray || [],
            groups: fetchedGroups,
            productsDTO: mappedProducts,
            products: mapProductsForAutocomplete(originalProducts),
            schemeConfigration: s.schemeConfigration || getInitialPayload(periodFrom, periodTo).schemeConfigration,
          });
        };

        setPayloadWithGroups();
      } else {
        setPayload(getInitialPayload(periodFrom, periodTo));
      }
    }
  }, [open, periodFrom, periodTo, editingScheme]);

  const handleClose = () => {
    setOpen(false);
    setPayload(getInitialPayload(periodFrom, periodTo));
    setIsDraft(false);
    setIsAiExtracted(false);
    setShowSaveConfirmModal(false);
    setPendingRequestPayload(null);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
  };

  const executeSave = (requestPayload) => {
    // If editing a draft and status changed to IN_REVIEW or ACTIVE, POST to published (save as new)
    if (isDraft && (requestPayload.status === "IN_REVIEW" || requestPayload.status === "ACTIVE")) {
      SchemesApi.AddSchemeV2(requestPayload)
        .then(() => {
          handleClose();
          showToast("Scheme saved to Published Schemes successfully", "success");
          if (onSaved) onSaved();
        })
        .catch((err) => {
          if (err?.response?.data) {
            showToast(`${err.response.status} ${err.response.data.message}`);
          } else {
            showToast("Error saving scheme");
          }
        });
    }
    // If editing published scheme, use PUT (update)
    else if (payload.id && !isDraft) {
      SchemesApi.UpdateSchemeV2({ id: payload.id, ...requestPayload })
        .then(() => {
          handleClose();
          showToast("Scheme updated successfully", "success");
          if (onSaved) onSaved();
        })
        .catch((err) => {
          if (err?.response?.data) {
            showToast(`${err.response.status} ${err.response.data.message}`);
          } else {
            showToast("Error updating scheme");
          }
        });
    }
    // If new scheme, POST
    else {
      SchemesApi.AddSchemeV2(requestPayload)
        .then(() => {
          handleClose();
          showToast("Scheme created successfully", "success");
          if (onSaved) onSaved();
        })
        .catch((err) => {
          if (err?.response?.data) {
            showToast(`${err.response.status} ${err.response.data.message}`);
          } else {
            showToast("Error creating scheme");
          }
        });
    }
  };

  const handleConfirmedSave = (chosenStatus) => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setShowSaveConfirmModal(false);
    setIsAiExtracted(false);
    const finalPayload = {
      ...pendingRequestPayload,
      status: chosenStatus,
      isActive: chosenStatus === "ACTIVE",
    };
    executeSave(finalPayload);
  };

  useEffect(() => {
    if (!showSaveConfirmModal) return;
    setSaveTimer(10);
    timerIntervalRef.current = setInterval(() => {
      setSaveTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, [showSaveConfirmModal]);

  useEffect(() => {
    if (showSaveConfirmModal && saveTimer === 0) {
      handleConfirmedSave("DRAFT");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTimer, showSaveConfirmModal]);

  const handleSave = () => {
    const isValid = payload.groupIds?.length > 0 && payload.brandId && payload.name;

    if (!isValid) {
      showToast("Please fill required fields: Name, Brand, and Groups");
      return;
    }

    // Draft validation: Cannot save draft with DRAFT status
    if (isDraft && payload.status === "DRAFT") {
      showToast("Draft must be saved as 'In Review' or 'Active'. Cannot save as Draft status.", "warning");
      return;
    }

    if (payload.productsDTO?.length > 0) {
      const productsWithNoSlabs = payload.productsDTO.filter(
        (p) => !p.slabs || p.slabs.length === 0
      );
      if (productsWithNoSlabs.length > 0) {
        const productNames = productsWithNoSlabs.map((p) => p.productName).join(", ");
        showToast(`Product(s) with no slabs: ${productNames}. Please add at least one slab to each product.`);
        return;
      }
    }

    const requestPayload = {
      name: payload.name,
      brandId: payload.brandId,
      imageUrl: payload.imageUrl || "",
      description: payload.description || "",
      termsAndConditions: payload.termsAndConditions || "",
      periodFrom: payload.periodFrom,
      periodTo: payload.periodTo,
      isActive: payload.status === "ACTIVE",
      status: payload.status || "DRAFT",
      ...(payload.isDeleted !== null && { isDeleted: payload.isDeleted }),
      ...(payload.isNew !== null && { isNew: payload.isNew }),
      ...(payload.isExpiring !== null && { isExpiring: payload.isExpiring }),
      groupIds: payload.groupIds,
      productsDTO: payload.productsDTO?.map((p) => ({
        productId: p.productId,
        productCode: p.productCode || "",
        slabs: p.slabs?.map((s) => {
          const processedSlab = { ...s };
          if (payload.schemeConfigration?.payoutCriteriaType !== "GIFT_REWARD_TRIP") {
            delete processedSlab.gift;
          }
          return processedSlab;
        }) || [],
      })) || [],
      schemeConfigration: {
        ...payload.schemeConfigration,
        payoutCriteriaTypePerUnitPoints: payload.schemeConfigration?.payoutCriteriaType === "PER_UNIT"
          ? payload.schemeConfigration?.payoutCriteriaTypePerUnitPoints
          : 0,
        payoutCriteriaTypeGift: (payload.schemeConfigration?.payoutCriteriaType === "GIFT_REWARD_TRIP")
          ? payload.schemeConfigration?.payoutCriteriaTypeGift
          : "",
      },
    };

    if (isAiExtracted) {
      setPendingRequestPayload(requestPayload);
      setShowSaveConfirmModal(true);
      return;
    }

    executeSave(requestPayload);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (field, value) => {
    setPayload((prev) => ({
      ...prev,
      schemeConfigration: { ...prev.schemeConfigration, [field]: value },
    }));
  };

  const fetchGroups = async (keyword) => {
    try {
      const { data } = await GroupsApi.GetGroupsListBySearch({ keyword, page: 0, size: 50 });
      return data.data || [];
    } catch (err) {
      showToast(err?.response?.data?.message || "Error fetching groups");
      return [];
    }
  };

  const fetchProducts = async (keyword) => {
    if (!payload.brandId) {
      showToast("Please select Brand first");
      return [];
    }

    try {
      const { data } = await ProductsApi.SearchProducts({
        page: 0,
        size: 10,
        keyword,
        brandId: payload.brandId,
        isActive: true,
      });
      return data.data || [];
    } catch (err) {
      showToast(err?.response?.data?.message || "Error fetching products");
      return [];
    }
  };

  const handleProductsChange = (_, value) => {
    setPayload((prev) => {
      const existingIds = prev.productsDTO?.map((p) => p.productId) || [];
      const newProductsDTO = value.map((p) => {
        const existingIndex = existingIds.indexOf(p.id);
        if (existingIndex !== -1) {
          return prev.productsDTO[existingIndex];
        }
        return {
          productId: p.id,
          productName: `${p.primaryName} ${p.secondaryName || ""}`.trim(),
          productCode: p.productCode || "",
          slabs: [],
          isNew: true,
        };
      });
      return { ...prev, productsDTO: newProductsDTO, products: value };
    });
  };

  const handleGroupsChange = (_, value) => {
    setPayload((prev) => ({
      ...prev,
      groupIds: value.map((g) => g.id),
      groups: value
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    try {
      const response = await SchemesApi.ExtractSchemesImageV2(formData);
      const extracted = response.data?.extractedScheme;

      // Map the extracted data to payload
      const mapExtractedProducts = (products) => {
        if (!products) return [];
        return products.map((p) => ({
          productId: p.productId || "",
          productName: `${p.product?.primaryName || ""} ${p.product?.secondaryName || ""}`.trim() || p.productName || "",
          productCode: p.productCode || "",
          slabs: p.slabs || [],
        }));
      };

      const mapExtractedGroups = (groups) => {
        if (groups && Array.isArray(groups) && groups.length > 0 && typeof groups[0] === 'object') {
          return groups;
        }
        return [];
      };

      setPayload((prev) => ({
        ...prev,
        id: extracted.id || prev.id,
        name: extracted.name || prev.name,
        brandId: extracted.brandId || prev.brandId,
        imageUrl: extracted.imageUrl || prev.imageUrl,
        description: extracted.description || prev.description,
        termsAndConditions: extracted.termsAndConditions || prev.termsAndConditions,
        periodFrom: extracted.periodFrom || prev.periodFrom,
        periodTo: extracted.periodTo || prev.periodTo,
        isActive: typeof extracted.isActive === "boolean" ? extracted.isActive : prev.isActive,
        status: extracted.status || prev.status || "DRAFT",
        isDeleted: typeof extracted.isDeleted === "boolean" ? extracted.isDeleted : prev.isDeleted,
        isNew: typeof extracted.isNew === "boolean" ? extracted.isNew : prev.isNew,
        isExpiring: typeof extracted.isExpiring === "boolean" ? extracted.isExpiring : prev.isExpiring,
        groupIds: extracted.groupIds || prev.groupIds,
        groups: mapExtractedGroups(extracted.groups),
        productsDTO: mapExtractedProducts(extracted.productsDTO || extracted.products || []),
        products: mapExtractedProducts(extracted.productsDTO || extracted.products || []),
        schemeConfigration: extracted.schemeConfigration || prev.schemeConfigration,
      }));

      if (response.data?.success && extracted) {
        showToast("Image extracted! Please review the data before saving.", "success");
        setIsAiExtracted(true);
      } else if (!response.data?.success && !extracted) {
        showToast(response.data?.errorMessage || "Failed to extract image. Saved as Draft.", "warning");
        setTimeout(() => {
          handleClose();
          if (onSaved) onSaved();
        }, 1500);
      } else {
        showToast(response.data?.errorMessage || "Image extracted with errors. Please review.", "warning");
      }
    } catch (err) {
      showToast(err?.response?.data?.errorMessage || "Error uploading image");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {payload.id ? "Edit Scheme" : "Add Scheme"}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <Box sx={{ mb: 3, p: 2, border: "1px dashed #ccc", borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Upload Image to Extract Scheme Data
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Choose & Upload Image"}
            </Button>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {isUploading ? "Processing image..." : "Select an image file to auto-fill form"}
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => handleImageUpload(e.target.files?.[0])}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="standard" sx={{ my: 2 }}>
              <InputLabel>Select Brand *</InputLabel>
              <Select
                required
                value={payload.brandId || ""}
                label="Brand"
                onChange={(e) => setPayload((prev) => ({ ...prev, brandId: e.target.value }))}
              >
                <MenuItem value="">-- Select Brand --</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              required
              fullWidth
              sx={{ my: 2 }}
              label="Scheme Name"
              name="name"
              variant="standard"
              value={payload.name}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              required
              fullWidth
              type="date"
              sx={{ my: 2 }}
              label="Start Date"
              variant="standard"
              InputLabelProps={{ shrink: true }}
              value={payload.periodFrom}
              onChange={(e) => setPayload((prev) => ({ ...prev, periodFrom: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              required
              fullWidth
              type="date"
              sx={{ my: 2 }}
              label="End Date"
              variant="standard"
              InputLabelProps={{ shrink: true }}
              value={payload.periodTo}
              onChange={(e) => setPayload((prev) => ({ ...prev, periodTo: e.target.value }))}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <FormLabel>Deleted</FormLabel>
              <RadioGroup
                row
                value={payload.isDeleted === null ? "" : payload.isDeleted}
                onChange={(e) => setPayload((prev) => ({ ...prev, isDeleted: e.target.value === "true" }))}
              >
                <FormControlLabel value={true} control={<Radio />} label="Yes" />
                <FormControlLabel value={false} control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="standard" sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={payload.status || "DRAFT"}
                onChange={(e) => setPayload((prev) => ({ ...prev, status: e.target.value }))}
              >
                {SCHEME_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <FormLabel>Scheme Status</FormLabel>
              <RadioGroup
                row
                value={payload.isNew === true ? "new" : payload.isExpiring === true ? "expiring" : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setPayload((prev) => ({
                    ...prev,
                    isNew: value === "new",
                    isExpiring: value === "expiring",
                  }));
                }}
              >
                <FormControlLabel value="new" control={<Radio />} label="New" />
                <FormControlLabel value="expiring" control={<Radio />} label="Expiring" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              sx={{ my: 2 }}
              label="Image URL"
              name="imageUrl"
              variant="standard"
              value={payload.imageUrl}
              onChange={handleFieldChange}
            />
            {payload.imageUrl && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, mb: 1 }}>
                <Box
                  component="a"
                  href={payload.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ display: "inline-block", flexShrink: 0 }}
                >
                  <Box
                    component="img"
                    src={payload.imageUrl}
                    alt="Preview"
                    sx={{
                      height: 72,
                      width: 120,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid #E2E8F0",
                      display: "block",
                    }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  href={payload.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  component="a"
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Open Preview ↗
                </Button>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              sx={{ my: 2 }}
              label="Description"
              name="description"
              variant="standard"
              value={payload.description}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              multiline
              rows={2}
              sx={{ my: 2 }}
              label="Terms and Conditions"
              name="termsAndConditions"
              variant="standard"
              value={payload.termsAndConditions}
              onChange={handleFieldChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Scheme Configuration</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              sx={{ my: 1 }}
              label="Config Name"
              variant="standard"
              value={payload.schemeConfigration?.schemeConfigName || ""}
              onChange={(e) => handleConfigChange("schemeConfigName", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              sx={{ my: 1 }}
              label="Config Period From"
              variant="standard"
              InputLabelProps={{ shrink: true }}
              value={payload.schemeConfigration?.periodFrom || ""}
              onChange={(e) => handleConfigChange("periodFrom", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              sx={{ my: 1 }}
              label="Config Period To"
              variant="standard"
              InputLabelProps={{ shrink: true }}
              value={payload.schemeConfigration?.periodTo || ""}
              onChange={(e) => handleConfigChange("periodTo", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard" sx={{ my: 1 }}>
              <InputLabel>Target Applied On</InputLabel>
              <Select
                value={payload.schemeConfigration?.targetAppliedOn || ""}
                onChange={(e) => handleConfigChange("targetAppliedOn", e.target.value)}
              >
                {TARGET_APPLIED_ON_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard" sx={{ my: 1 }}>
              <InputLabel>Target Achievement Type</InputLabel>
              <Select
                value={payload.schemeConfigration?.targetAchievementType || ""}
                onChange={(e) => handleConfigChange("targetAchievementType", e.target.value)}
              >
                {TARGET_ACHIEVEMENT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="standard" sx={{ my: 1 }}>
              <InputLabel>Payout Criteria Type</InputLabel>
              <Select
                value={payload.schemeConfigration?.payoutCriteriaType || ""}
                onChange={(e) => handleConfigChange("payoutCriteriaType", e.target.value)}
              >
                {PAYOUT_CRITERIA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {payload.schemeConfigration?.payoutCriteriaType === "PER_UNIT" && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                sx={{ my: 1 }}
                label="Per Unit Points (Payout Criteria)"
                variant="standard"
                value={payload.schemeConfigration?.payoutCriteriaTypePerUnitPoints || ""}
                onChange={(e) => handleConfigChange("payoutCriteriaTypePerUnitPoints", parseFloat(e.target.value) || 0)}
              />
            </Grid>
          )}

          {payload.schemeConfigration?.payoutCriteriaType === "GIFT_REWARD_TRIP" && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                sx={{ my: 1 }}
                label="Gift (Payout Criteria)"
                variant="standard"
                value={payload.schemeConfigration?.payoutCriteriaTypeGift || ""}
                onChange={(e) => handleConfigChange("payoutCriteriaTypeGift", e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard" sx={{ my: 1 }}>
              <InputLabel>Payout Sell Type</InputLabel>
              <Select
                value={payload.schemeConfigration?.payoutSellType || ""}
                onChange={(e) => handleConfigChange("payoutSellType", e.target.value)}
              >
                {PAYOUT_SELL_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              sx={{ my: 1 }}
              label="Payout Selection Help Text"
              variant="standard"
              value={payload.schemeConfigration?.payoutSelectionHelpText || ""}
              onChange={(e) => handleConfigChange("payoutSelectionHelpText", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Groups & Products</Typography>
          </Grid>

          <Grid item xs={12}>
            <AutoComplete
              sx={{ my: 2 }}
              label="Select Groups *"
              getOptionLabel={(option) => option.name}
              value={payload.groups}
              fetchOptions={fetchGroups}
              onChange={handleGroupsChange}
            />
          </Grid>

          <Grid item xs={12}>
            <AutoComplete
              sx={{ my: 2 }}
              label="Select Products"
              getOptionLabel={(option) => `${option.primaryName} ${option.secondaryName || ""}`.trim()}
              value={payload.products}
              fetchOptions={fetchProducts}
              onChange={handleProductsChange}
            />
          </Grid>
        </Grid>

        <div className="overflow-x-auto">
          <ProductsSlabTable payload={payload} setPayload={setPayload} fetchProducts={fetchProducts} />
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave}>{isDraft ? "Save" : (payload.id ? "Update" : "Save")}</Button>
      </DialogActions>

      <Dialog open={showSaveConfirmModal} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>How would you like to save this scheme?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The AI has extracted and filled in the scheme data. Choose whether to publish it as
            <strong> Active</strong> or keep it as a <strong>Draft</strong> for later review.
          </Typography>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Auto-saving as Draft in {saveTimer}s...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {saveTimer}s
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(saveTimer / 10) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => handleConfirmedSave("DRAFT")}
            sx={{ flex: 1 }}
          >
            Save as Draft
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleConfirmedSave("ACTIVE")}
            sx={{ flex: 1 }}
          >
            Save as Active
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddSchemeModal;
